import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { BpgEventsService } from '../../bpg-events/bpg-events.service';
import { HoshinBudgetService } from '../../bpg-hoshin/hoshin-budget.service';
import { SelfService } from '../../bpg-self-services/self-services.service.ts';
import { OmniViewMode } from '../omnidashboard.config';
import { ChapterInfo, MatrixCellData, MetricData } from '../omnidashboard.models';
import { classifyApiError } from './omnidashboard-error';
import { buildFiscalYearOptions } from './omnidashboard-fiscal-year';
import {
  createInitialOmnidashboardState,
  OmnidashboardState,
} from './omnidashboard-state.model';
import { OmnidashboardStorageService } from './omnidashboard-storage.service';
import { OmniFilterState } from '../filters/omnidashboard-filters.model';
import { resolveOmniFilters, sanitizeFilterSelections } from '../filters/omnidashboard-filters';
import {
  applyMetricToAllCells,
  buildMatrixFromChapters,
  buildMatrixViewModel,
  createEmptyMatrixViewModel,
} from '../matrix/omnidashboard-matrix-view';
import { buildMatrixGrid } from '../matrix/omnidashboard-matrix';
import { DEFAULT_OMNI_VIEW, getMetricLoader } from '../metrics/metric-registry';

/** Owns omnidashboard state, API orchestration, and matrix/metric recomputation. */
@Injectable()
export class OmnidashboardFacadeService {
  private state: OmnidashboardState;

  constructor(
    private readonly bpgEventsService: BpgEventsService,
    private readonly selfService: SelfService,
    private readonly omniStorage: OmnidashboardStorageService,
    private readonly hoshinBudgetService: HoshinBudgetService
  ) {
    const fy = buildFiscalYearOptions();
    this.state = createInitialOmnidashboardState(fy.options, fy.selectedFiscalYear);
  }

  getState(): OmnidashboardState {
    return this.state;
  }

  initFiscalYearFromStorage(): void {
    const savedFy = this.omniStorage.getSelectedFiscalYear();
    if (savedFy) {
      this.state = { ...this.state, selectedFiscalYear: savedFy };
    }
  }

  setActiveView(view: OmniViewMode): void {
    this.state = { ...this.state, activeView: view };
    if (this.state.matrix.siteColumns.length > 0 || this.state.matrix.ergRows.length > 0) {
      this.rebuildViewModel(this.state.filters);
    }
  }

  updateFilters(filters: OmniFilterState): void {
    this.rebuildViewModel(filters);
  }

  clearFilters(): void {
    this.rebuildViewModel({ selectedSiteNames: [], selectedErgNames: [] });
  }

  persistFiscalYear(): void {
    this.omniStorage.setSelectedFiscalYear(this.state.selectedFiscalYear);
  }

  loadMatrix(
    userId: unknown,
    roleId: string | null,
    onComplete?: () => void
  ): Observable<ChapterInfo[]> {
    this.persistFiscalYear();
    this.state = {
      ...this.state,
      isLoading: true,
      isEmptyResult: false,
      errors: { hasTimeoutError: false, hasApiError: false },
    };

    const dto = { userId, roleId, fiscalYear: this.state.selectedFiscalYear };

    return this.bpgEventsService.getChapterDropDown(dto).pipe(
      tap((chapters: ChapterInfo[]) => {
        if (!chapters || chapters.length === 0) {
          this.state = {
            ...this.state,
            isEmptyResult: true,
            chapters: [],
            filters: { selectedSiteNames: [], selectedErgNames: [] },
            matrix: createEmptyMatrixViewModel(),
            loadedMetrics: new Set<OmniViewMode>([DEFAULT_OMNI_VIEW]),
            filterAnnounceMessage: '',
            siteFilterTooltip: '',
            ergFilterTooltip: '',
          };
          return;
        }

        const built = buildMatrixFromChapters(chapters);
        const filters = { selectedSiteNames: [], selectedErgNames: [] };
        const matrix = buildMatrixViewModel(
          this.state.activeView,
          filters,
          built.siteColumns,
          built.ergRows,
          built.matrixCellMap,
          built.matrixGrid
        );
        const resolvedFilters = resolveOmniFilters(
          filters,
          built.siteColumns,
          built.ergRows,
          matrix.visibleChapterCount
        );

        this.state = {
          ...this.state,
          chapters,
          filters: {
            selectedSiteNames: resolvedFilters.selectedSiteNames,
            selectedErgNames: resolvedFilters.selectedErgNames,
          },
          matrix,
          loadedMetrics: new Set<OmniViewMode>([DEFAULT_OMNI_VIEW]),
          filterAnnounceMessage: resolvedFilters.filterAnnounceMessage,
          siteFilterTooltip: resolvedFilters.siteFilterTooltip,
          ergFilterTooltip: resolvedFilters.ergFilterTooltip,
        };
      }),
      catchError((error: unknown) => {
        this.state = {
          ...this.state,
          errors: classifyApiError(error),
        };
        return throwError(() => error);
      }),
      finalize(() => {
        this.state = { ...this.state, isLoading: false, isMetricLoading: false };
        if (onComplete) {
          onComplete();
        }
      })
    );
  }

  loadMetric(): Observable<void> {
    const view = this.state.activeView;
    if (this.state.loadedMetrics.has(view)) {
      // Metric data already on cells; only re-render for the active view or filters.
      this.rebuildViewModel(this.state.filters);
      return of(undefined);
    }

    // Show default metric placeholders until the loader resolves (e.g. ack N/A cells).
    this.rebuildViewModel(this.state.filters);

    const resolverFactory = this.getMetricResolver(view);
    if (!resolverFactory) {
      // chapter-status is seeded in buildMatrixCellMap; no API loader registered.
      return of(undefined);
    }

    this.state = {
      ...this.state,
      isMetricLoading: true,
      errors: { ...this.state.errors, hasApiError: false },
    };

    return resolverFactory.pipe(
      tap((resolver: (cell: MatrixCellData) => MetricData) => {
        const updatedMap = applyMetricToAllCells(
          this.state.matrix.matrixCellMap,
          view,
          resolver
        );
        const loadedMetrics = new Set(this.state.loadedMetrics);
        loadedMetrics.add(view);

        this.state = {
          ...this.state,
          loadedMetrics,
          errors: { hasTimeoutError: false, hasApiError: false },
        };
        this.rebuildViewModel(this.state.filters, updatedMap);
      }),
      map(() => undefined),
      catchError((error: unknown) => {
        this.state = {
          ...this.state,
          errors: classifyApiError(error),
          isMetricLoading: false,
        };
        return of(undefined);
      }),
      finalize(() => {
        this.state = { ...this.state, isMetricLoading: false };
      })
    );
  }

  isFilterActive(): boolean {
    const { filters, matrix } = this.state;
    // Empty selection means "all"; filtering only when a strict subset is selected.
    const sitesFiltered =
      filters.selectedSiteNames.length > 0 &&
      filters.selectedSiteNames.length < matrix.siteColumns.length;
    const ergsFiltered =
      filters.selectedErgNames.length > 0 &&
      filters.selectedErgNames.length < matrix.ergRows.length;
    return sitesFiltered || ergsFiltered;
  }

  hasNoVisibleMatrix(): boolean {
    const { matrix } = this.state;
    return matrix.ergRows.length > 0 && (
      matrix.visibleSiteColumns.length === 0 || matrix.visibleErgRows.length === 0
    );
  }

  private rebuildViewModel(
    filters: OmniFilterState,
    matrixCellMap?: Map<string, MatrixCellData>
  ): void {
    const { matrix, activeView } = this.state;
    const cellMap = matrixCellMap || matrix.matrixCellMap;
    // When metric data changes, rebuild the grid from the updated cell map.
    const matrixGrid = matrixCellMap
      ? buildMatrixGrid(matrix.ergRows, matrix.siteColumns, cellMap)
      : matrix.matrixGrid;
    const sanitized = sanitizeFilterSelections(filters, matrix.siteColumns, matrix.ergRows);
    const viewModel = buildMatrixViewModel(
      activeView,
      sanitized,
      matrix.siteColumns,
      matrix.ergRows,
      cellMap,
      matrixGrid
    );
    const resolvedFilters = resolveOmniFilters(
      sanitized,
      matrix.siteColumns,
      matrix.ergRows,
      viewModel.visibleChapterCount
    );

    this.state = {
      ...this.state,
      filters: {
        selectedSiteNames: resolvedFilters.selectedSiteNames,
        selectedErgNames: resolvedFilters.selectedErgNames,
      },
      matrix: viewModel,
      filterAnnounceMessage: resolvedFilters.filterAnnounceMessage,
      siteFilterTooltip: resolvedFilters.siteFilterTooltip,
      ergFilterTooltip: resolvedFilters.ergFilterTooltip,
    };
  }

  private getMetricResolver(
    view: OmniViewMode
  ): Observable<(cell: MatrixCellData) => MetricData> | null {
    const loader = getMetricLoader(view);
    if (!loader) {
      return null;
    }
    return loader.load({
      selfService: this.selfService,
      hoshinBudgetService: this.hoshinBudgetService,
      selectedFiscalYear: this.state.selectedFiscalYear,
    });
  }
}
